import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { forumAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { VerificationBadge, CategoryBadge, RoleBadge } from '../components/Badges';
import {
  ArrowLeft, User, Clock, MessageSquare, Flag, Send,
  Loader2, AlertTriangle, MoreVertical, Trash2, Edit
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const ForumPostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPost();
  }, [postId]);

  const loadPost = async () => {
    try {
      const [postRes, repliesRes] = await Promise.all([
        forumAPI.getPost(postId),
        forumAPI.getReplies(postId),
      ]);
      setPost(postRes.data);
      setReplies(repliesRes.data);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/backlot');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setSubmitting(true);
    try {
      await forumAPI.createReply(postId, { body: replyContent });
      toast.success('Reply posted!');
      setReplyContent('');
      loadPost();
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async () => {
    const reason = prompt('Why are you reporting this post?');
    if (!reason) return;

    try {
      await forumAPI.reportPost(postId, reason);
      toast.success('Post reported. Our team will review it.');
    } catch (error) {
      toast.error('Failed to report post');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await forumAPI.deletePost(postId);
      toast.success('Post deleted');
      navigate('/backlot');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) return null;

  const isAuthor = user?.id === post.author_user_id;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/backlot')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Backlot
        </Button>

        {/* Post */}
        <Card className="border-white/5 bg-card/50 mb-6 animate-fade-in" data-testid="post-detail-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <CategoryBadge category={post.category} />
                {post.category === 'fraud_alerts' && (
                  <span className="flex items-center gap-1 text-red-400 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    Fraud Alert
                  </span>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="post-menu-btn">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isAuthor && (
                    <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Post
                    </DropdownMenuItem>
                  )}
                  {isAuthenticated && !isAuthor && (
                    <DropdownMenuItem onClick={handleReport}>
                      <Flag className="w-4 h-4 mr-2" />
                      Report Post
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide mb-4">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">{post.author_profile?.full_name || 'Anonymous'}</p>
                  <div className="flex items-center gap-2">
                    {post.author_profile?.role && (
                      <RoleBadge role={post.author_profile.role} />
                    )}
                    {post.author_profile && (
                      <VerificationBadge
                        status={post.author_profile.verification_status || 'pending'}
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              </div>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatTime(post.created_at)}
              </span>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="whitespace-pre-wrap text-foreground">{post.body}</p>
            </div>
          </CardContent>
        </Card>

        {/* Reply Form */}
        {isAuthenticated && (
          <Card className="border-white/5 bg-card/50 mb-6 animate-fade-in delay-100" data-testid="reply-form-card">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-3">Add a Reply</h3>
              <Textarea
                placeholder="Share your thoughts..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={4}
                className="mb-3 bg-secondary/30 border-white/10"
                data-testid="reply-input"
              />
              <Button
                onClick={handleSubmitReply}
                disabled={submitting}
                className="glow-gold"
                data-testid="submit-reply-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Post Reply
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Replies */}
        <div className="space-y-4">
          <h3 className="font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            {replies.length} Replies
          </h3>
          {replies.length === 0 ? (
            <Card className="border-white/5 bg-card/50">
              <CardContent className="py-8 text-center">
                <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No replies yet. Be the first to respond!</p>
              </CardContent>
            </Card>
          ) : (
            replies.map((reply, index) => (
              <Card
                key={reply.id}
                className="border-white/5 bg-card/50 animate-fade-in"
                style={{ animationDelay: `${(index + 2) * 100}ms` }}
                data-testid={`reply-${reply.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium">
                          {reply.author_profile?.full_name || 'Anonymous'}
                        </span>
                        {reply.author_profile && (
                          <VerificationBadge
                            status={reply.author_profile.verification_status || 'pending'}
                            size="sm"
                          />
                        )}
                        <span className="text-sm text-muted-foreground">
                          {formatTime(reply.created_at)}
                        </span>
                      </div>
                      <p className="text-foreground whitespace-pre-wrap">{reply.body}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ForumPostDetailPage;
